/**
 * Paper Trading Positions Table Row Models.
 *
 * Reconciles open/closed positions, applies sorting (open-first pinned, column key sorting),
 * computes P&L metrics (realized, unrealized, total, percentage), and prepares table row view models.
 */

import { formatCurrencyInr, formatNumberInr } from "./format";
import {
  posBuyAvg,
  posIsOpen,
  posNetQty,
  posOpenAvg,
  posPnlPct,
  posSellAvg,
  posSide,
  posUnrealizedPnl,
} from "./positions";

export interface SortOptions {
  key?: string | null;
  dir?: "asc" | "desc" | null;
}

export interface PaperPositionRowModel {
  raw: any;
  index: number;
  symbol: string;
  side: string;
  sideDisplay: string;
  netQty: number;
  avg: number | null;
  avgFormatted: string;
  ltp: number | null;
  ltpFormatted: string;
  realized: number;
  realizedFormatted: string;
  realizedClass: string;
  unrealized: number;
  unrealizedFormatted: string;
  unrealizedClass: string;
  total: number;
  totalFormatted: string;
  totalClass: string;
  pnlPct: number | null;
  pnlPctText: string;
  pnlPctClass: string;
  buyQty: number;
  buyPrice: number | null;
  buyPriceFormatted: string;
  sellQty: number;
  sellPrice: number | null;
  sellPriceFormatted: string;
  isOpen: boolean;
}

/**
 * Builds table row view models for the paper trading positions tab.
 */
export function buildPaperPositionRowModels(
  positions: any[],
  getLtpForSymbol: (symbol: string) => number | null,
  sort: SortOptions,
  isTodayFn: (dateIso?: string) => boolean,
): PaperPositionRowModel[] {
  if (!Array.isArray(positions)) return [];

  const visible = positions
    .map((p, originalIndex) => ({ p, originalIndex }))
    .filter(({ p }) => posIsOpen(p) || isTodayFn(p.closedAtISO));

  const rows = visible.map(({ p, originalIndex }) => {
    const ltp = getLtpForSymbol(p.symbol);
    const isOpen = posIsOpen(p);
    const avg = posOpenAvg(p);
    const unrealized = posUnrealizedPnl(p, ltp);
    const realized = p.realizedGross || 0;
    const total = unrealized + realized;
    const pnlPct = posPnlPct(p, ltp, realized);
    const side = posSide(p);
    const netQty = Math.abs(posNetQty(p));
    const buyPrice = posBuyAvg(p);
    const sellPrice = posSellAvg(p);

    const pnlPctText =
      pnlPct != null && !isNaN(pnlPct)
        ? `${pnlPct >= 0 ? "+" : ""}${pnlPct.toFixed(2)}%`
        : "--";
    const pnlPctClass =
      pnlPct != null && !isNaN(pnlPct)
        ? pnlPct >= 0
          ? "positive"
          : "negative"
        : "";

    const formatPnlNumberOnly = (n: number) => {
      const formatted = formatCurrencyInr(n);
      return (n >= 0 ? "+" : "") + formatted.replace("₹", "");
    };

    return {
      raw: p,
      index: originalIndex,
      symbol: p.symbol || "",
      side,
      sideDisplay: side === "FLAT" ? "--" : side,
      netQty,
      avg,
      avgFormatted: avg != null ? formatNumberInr(avg, 2, "--") : "--",
      ltp,
      ltpFormatted: ltp != null ? formatNumberInr(ltp, 2, "--") : "--",
      realized,
      realizedFormatted: formatPnlNumberOnly(realized),
      realizedClass: realized >= 0 ? "positive" : "negative",
      unrealized,
      unrealizedFormatted: formatPnlNumberOnly(unrealized),
      unrealizedClass: unrealized >= 0 ? "positive" : "negative",
      total,
      totalFormatted: formatPnlNumberOnly(total),
      totalClass: total >= 0 ? "positive" : "negative",
      pnlPct,
      pnlPctText,
      pnlPctClass,
      buyQty: p.buyQty || 0,
      buyPrice,
      buyPriceFormatted:
        buyPrice != null ? formatNumberInr(buyPrice, 2, "--") : "--",
      sellQty: p.sellQty || 0,
      sellPrice,
      sellPriceFormatted:
        sellPrice != null ? formatNumberInr(sellPrice, 2, "--") : "--",
      isOpen,
    };
  });

  // Open positions first; sort within groups
  rows.sort((a, b) => {
    if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
    if (!sort || !sort.key) return 0;
    const av = (a as any)[sort.key] ?? 0;
    const bv = (b as any)[sort.key] ?? 0;
    const multiplier = sort.dir === "asc" ? 1 : -1;
    return (av > bv ? 1 : av < bv ? -1 : 0) * multiplier;
  });

  return rows;
}
